import React, { Component } from "react";
import PropTypes from "prop-types";
import { format } from "d3-format";
import { timeFormat } from "d3-time-format";
import displayValuesFor from "./displayValuesFor";
import GenericChartComponent from "../GenericChartComponent";

import { isDefined, functor } from "../utils";
import ToolTipText from "./ToolTipText";
import ToolTipTSpanLabel from "./ToolTipTSpanLabel";

// Canvas-based text measurement utility
let measureCanvas;
function getTextWidth(text, fontFamily = "Arial", fontSize = 12) {
  if (!measureCanvas) {
    measureCanvas = document.createElement("canvas");
  }
  const context = measureCanvas.getContext("2d");
  context.font = `${fontSize}px ${fontFamily}`;
  const metrics = context.measureText(text);
  return metrics.width;
}

// Constants
const TOOLTIP_BG_HEIGHT = 18;
const TOOLTIP_BG_PADDING = 6;

class OHLCTooltip extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
  }
  renderSVG(moreProps) {
    const { displayValuesFor } = this.props;
    const {
      xDisplayFormat,
      accessor,
      volumeFormat,
      tickCountFormat, // NEW: Formatter for tick count
      financialVolumeFormat, // NEW: Formatter for financial volume
      ohlcFormat,
      percentFormat,
      displayTexts,
    } = this.props;

    const {
      chartConfig: { width, height },
    } = moreProps;
    const { displayXAccessor } = moreProps;

    const currentItem = displayValuesFor(this.props, moreProps);

    let displayDate,
      open,
      high,
      low,
      close,
      volume,
      tickCount,
      financialVolume,
      percent;
    displayDate =
      open =
      high =
      low =
      close =
      volume =
      tickCount =
      financialVolume =
      percent =
        displayTexts.na;

    if (isDefined(currentItem) && isDefined(accessor(currentItem))) {
      const item = accessor(currentItem);

      // Extract numeric volume from processed volume object
      let numericVolume = null;
      if (isDefined(item.volume)) {
        if (typeof item.volume === "number") {
          numericVolume = item.volume;
        } else if (item.volume && typeof item.volume.volume === "number") {
          numericVolume = item.volume.volume;
        }
      }
      volume =
        numericVolume !== null ? volumeFormat(numericVolume) : displayTexts.na;

      // Extract and format tick count (NEW)
      let numericTickCount = null;
      if (isDefined(item.tickCount)) {
        numericTickCount =
          typeof item.tickCount === "number" ? item.tickCount : 0;
      }
      tickCount =
        numericTickCount !== null
          ? tickCountFormat(numericTickCount)
          : displayTexts.na;

      // Extract and format financial volume (NEW)
      let numericFinancialVolume = null;
      if (isDefined(item.financialVolume)) {
        numericFinancialVolume =
          typeof item.financialVolume === "number" ? item.financialVolume : 0;
      }
      financialVolume =
        numericFinancialVolume !== null
          ? financialVolumeFormat(numericFinancialVolume)
          : displayTexts.na;

      displayDate = xDisplayFormat(displayXAccessor(item));
      open = ohlcFormat(item.open);
      high = ohlcFormat(item.high);
      low = ohlcFormat(item.low);
      close = ohlcFormat(item.close);
      percent = percentFormat((item.close - item.open) / item.open);
    }

    const { origin: originProp } = this.props;
    const origin = functor(originProp);
    const [x, y] = origin(width, height);

    const itemsToDisplay = {
      displayDate,
      open,
      high,
      low,
      close,
      percent,
      volume,
      tickCount, // NEW: Include tick count
      financialVolume, // NEW: Include financial volume
      x,
      y,
      chartWidth: width,
    };
    return this.props.children(this.props, moreProps, itemsToDisplay);
  }
  render() {
    return (
      <GenericChartComponent
        clip={false}
        svgDraw={this.renderSVG}
        drawOn={["mousemove"]}
      />
    );
  }
}

OHLCTooltip.propTypes = {
  className: PropTypes.string,
  accessor: PropTypes.func,
  xDisplayFormat: PropTypes.func,
  children: PropTypes.func,
  volumeFormat: PropTypes.func,
  percentFormat: PropTypes.func,
  ohlcFormat: PropTypes.func,
  origin: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  fontFamily: PropTypes.string,
  fontSize: PropTypes.number,
  onClick: PropTypes.func,
  displayValuesFor: PropTypes.func,
  textFill: PropTypes.string,
  labelFill: PropTypes.string,
  bgFill: PropTypes.string,
  displayTexts: PropTypes.object,
};

const displayTextsDefault = {
  d: "Data: ",
  o: " O: ",
  h: " H: ",
  l: " L: ",
  c: " C: ",
  v: " Qtd: ", // Renamed from " Vol: " for shorter, cleaner labels
  vn: " Neg: ", // NEW: Tick count label
  vf: " Fin: ", // NEW: Financial volume label
  na: "n/a",
};

OHLCTooltip.defaultProps = {
  accessor: (d) => {
    return {
      date: d.date,
      open: d.open,
      high: d.high,
      low: d.low,
      close: d.close,
      volume: d.volume,
      tickCount: d.tickCount || 0, // NEW: Number of trades
      financialVolume: d.financialVolume || 0, // NEW: Financial volume
    };
  },
  xDisplayFormat: timeFormat("%d/%m/%Y %H:%M"),
  volumeFormat: (d) => {
    // Format volume with Portuguese locale (dot for thousands, comma for decimals)
    return d.toLocaleString("pt-BR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  },
  tickCountFormat: (d) => {
    // Format tick count (number of trades)
    if (!isFinite(d) || d === null || d === undefined) return "0";
    return d.toLocaleString("pt-BR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  },
  financialVolumeFormat: (d) => {
    // Format financial volume as currency with SI suffixes
    if (!isFinite(d) || d === null || d === undefined) return "R$ 0.00";
    if (d >= 1000000000) {
      return `R$ ${(d / 1000000000).toFixed(2)}B`;
    } else if (d >= 1000000) {
      return `R$ ${(d / 1000000).toFixed(2)}M`;
    } else if (d >= 1000) {
      return `R$ ${(d / 1000).toFixed(2)}K`;
    }
    return `R$ ${d.toFixed(2)}`;
  },
  percentFormat: format(".2%"),
  ohlcFormat: format(".2f"),
  displayValuesFor: displayValuesFor,
  origin: [0, 0],
  children: defaultDisplay,
  displayTexts: displayTextsDefault,
};

function defaultDisplay(props, moreProps, itemsToDisplay) {
  const {
    className,
    textFill,
    labelFill,
    bgFill,
    onClick,
    fontFamily,
    fontSize,
    displayTexts,
  } = props;

  const {
    displayDate,
    open,
    high,
    low,
    close,
    volume,
    tickCount,
    financialVolume,
    x,
    y,
    chartWidth,
  } = itemsToDisplay;

  const shouldHideField = (value) => value === displayTexts.na;

  // Width-based display tiers
  const effectiveWidth = chartWidth || 9999;
  const showFinancials = effectiveWidth >= 700; // Neg, Fin fields
  const showVolume = effectiveWidth >= 400; // Qtd field
  const twoRows = effectiveWidth < 400; // Date on row 1, OHLC on row 2

  // Build elements for row 1 (always Date, plus OHLC if single-row)
  const row1Elements = [];
  if (!shouldHideField(displayDate)) {
    row1Elements.push(
      <ToolTipTSpanLabel fill={labelFill} key="label" x={0} dy="5">
        {displayTexts.d}
      </ToolTipTSpanLabel>,
    );
    row1Elements.push(
      <tspan key="value" fill={textFill}>
        {displayDate}
      </tspan>,
    );
  }

  // OHLC fields go to row1 (single-row) or row2 (two-row)
  const ohlcElements = [];
  if (!shouldHideField(open)) {
    ohlcElements.push(
      <ToolTipTSpanLabel fill={labelFill} key="label_O">
        {displayTexts.o}
      </ToolTipTSpanLabel>,
    );
    ohlcElements.push(
      <tspan key="value_O" fill={textFill}>
        {open}
      </tspan>,
    );
  }
  if (!shouldHideField(high)) {
    ohlcElements.push(
      <ToolTipTSpanLabel fill={labelFill} key="label_H">
        {displayTexts.h}
      </ToolTipTSpanLabel>,
    );
    ohlcElements.push(
      <tspan key="value_H" fill={textFill}>
        {high}
      </tspan>,
    );
  }
  if (!shouldHideField(low)) {
    ohlcElements.push(
      <ToolTipTSpanLabel fill={labelFill} key="label_L">
        {displayTexts.l}
      </ToolTipTSpanLabel>,
    );
    ohlcElements.push(
      <tspan key="value_L" fill={textFill}>
        {low}
      </tspan>,
    );
  }
  if (!shouldHideField(close)) {
    ohlcElements.push(
      <ToolTipTSpanLabel fill={labelFill} key="label_C">
        {displayTexts.c}
      </ToolTipTSpanLabel>,
    );
    ohlcElements.push(
      <tspan key="value_C" fill={textFill}>
        {close}
      </tspan>,
    );
  }
  if (showVolume && !shouldHideField(volume)) {
    ohlcElements.push(
      <ToolTipTSpanLabel fill={labelFill} key="label_Vol">
        {displayTexts.v}
      </ToolTipTSpanLabel>,
    );
    ohlcElements.push(
      <tspan key="value_Vol" fill={textFill}>
        {volume}
      </tspan>,
    );
  }
  if (showFinancials && !shouldHideField(tickCount)) {
    ohlcElements.push(
      <ToolTipTSpanLabel fill={labelFill} key="label_TC">
        {displayTexts.vn}
      </ToolTipTSpanLabel>,
    );
    ohlcElements.push(
      <tspan key="value_TC" fill={textFill}>
        {tickCount}
      </tspan>,
    );
  }
  if (showFinancials && !shouldHideField(financialVolume)) {
    ohlcElements.push(
      <ToolTipTSpanLabel fill={labelFill} key="label_FV">
        {displayTexts.vf}
      </ToolTipTSpanLabel>,
    );
    ohlcElements.push(
      <tspan key="value_FV" fill={textFill}>
        {financialVolume}
      </tspan>,
    );
  }

  // In two-row mode, add x/dy to first OHLC element for proper positioning
  if (twoRows && ohlcElements.length > 0) {
    const firstEl = ohlcElements[0];
    ohlcElements[0] = React.cloneElement(firstEl, { x: 0, dy: "5" });
  }

  // Calculate background dimensions
  const bgPadding = TOOLTIP_BG_PADDING;
  const font = fontFamily || "Arial";
  const size = fontSize || 12;
  let bgWidth, bgHeight;

  if (twoRows) {
    bgHeight = TOOLTIP_BG_HEIGHT * 2;
    // Row 1: Date
    const r1Parts = [];
    if (!shouldHideField(displayDate))
      r1Parts.push(`${displayTexts.d}${displayDate}`);
    // Row 2: OHLC
    const r2Parts = [];
    if (!shouldHideField(open)) r2Parts.push(`${displayTexts.o}${open}`);
    if (!shouldHideField(high)) r2Parts.push(`${displayTexts.h}${high}`);
    if (!shouldHideField(low)) r2Parts.push(`${displayTexts.l}${low}`);
    if (!shouldHideField(close)) r2Parts.push(`${displayTexts.c}${close}`);
    const r1Width = getTextWidth(r1Parts.join(""), font, size);
    const r2Width = getTextWidth(r2Parts.join(""), font, size);
    bgWidth = Math.max(r1Width, r2Width) + bgPadding * 2;
  } else {
    bgHeight = TOOLTIP_BG_HEIGHT;
    // Single row: all visible fields
    const parts = [];
    if (!shouldHideField(displayDate))
      parts.push(`${displayTexts.d}${displayDate}`);
    if (!shouldHideField(open)) parts.push(`${displayTexts.o}${open}`);
    if (!shouldHideField(high)) parts.push(`${displayTexts.h}${high}`);
    if (!shouldHideField(low)) parts.push(`${displayTexts.l}${low}`);
    if (!shouldHideField(close)) parts.push(`${displayTexts.c}${close}`);
    if (showVolume && !shouldHideField(volume))
      parts.push(`${displayTexts.v}${volume}`);
    if (showFinancials && !shouldHideField(tickCount))
      parts.push(`${displayTexts.vn}${tickCount}`);
    if (showFinancials && !shouldHideField(financialVolume))
      parts.push(`${displayTexts.vf}${financialVolume}`);
    bgWidth = getTextWidth(parts.join(""), font, size) + bgPadding * 2;
  }

  // Merge OHLC into row1 for single-row mode
  const finalRow1 = twoRows ? row1Elements : [...row1Elements, ...ohlcElements];

  return (
    <g
      className={`react-stockcharts-tooltip-hover ${className}`}
      transform={`translate(${x}, ${y})`}
      onClick={onClick}
      data-evo-id="tooltip-ohlc"
    >
      <rect
        x={-bgPadding}
        y={1}
        width={bgWidth}
        height={bgHeight}
        fill={bgFill || "#1f2937"}
        rx={6}
        ry={6}
      />
      <ToolTipText x={0} y={0} fontFamily={fontFamily} fontSize={fontSize}>
        {finalRow1}
      </ToolTipText>
      {twoRows && ohlcElements.length > 0 && (
        <ToolTipText
          x={0}
          y={TOOLTIP_BG_HEIGHT}
          fontFamily={fontFamily}
          fontSize={fontSize}
        >
          {ohlcElements}
        </ToolTipText>
      )}
    </g>
  );
}

export default OHLCTooltip;
